import { cpuCapacity, validateCpuWorkers } from "./cpu-capacity";
import { parseUint256, MAX_UINT256 } from "./pow-v1";
import { workDeadlineReached } from "./work-deadline";
import type { BatchRequest, BatchResult, MiningBackend } from "./types";

const CHUNK_SIZE = 4096;

/** Splits a durably reserved range; never allocates nonces outside that range. */
export class CpuWorkerPool implements MiningBackend {
  readonly mode = "CPU" as const;
  private workers: MiningBackend[] = [];
  private active: AbortController | null = null;
  private disposed = false;
  get workerCount(): number { return this.workers.length; }
  get preferredBatchSize(): number { return CHUNK_SIZE * 4 * this.workerCount; }

  constructor(private readonly createWorker: () => MiningBackend,
    private readonly limit = cpuCapacity().workerLimit,
    private readonly now = () => performance.now()) {}

  async initialize(count: number): Promise<void> {
    validateCpuWorkers(count, this.limit);
    if (this.disposed || this.workers.length) throw new Error("CPU_POOL_ALREADY_INITIALIZED");
    const workers: MiningBackend[] = [];
    try {
      // Keep all instances for cleanup, including a partially created pool.
      for (let i = 0; i < count; i++) workers.push(this.createWorker());
      this.workers = workers;
      const initialized = await Promise.allSettled(workers.map(worker => Promise.resolve().then(() => worker.initialize(1))));
      const failure = initialized.find(result => result.status === "rejected");
      if (failure?.status === "rejected") throw failure.reason;
      if (this.disposed) throw new Error("CPU_POOL_DISPOSED");
    } catch (error) {
      await Promise.allSettled(workers.map(worker => Promise.resolve().then(() => worker.dispose())));
      this.workers = [];
      throw error;
    }
  }

  async runBatch(request: BatchRequest, signal: AbortSignal,
    onProgress?: (completed: number) => void): Promise<BatchResult> {
    if (this.disposed || !this.workers.length || this.active) throw new Error("CPU_POOL_NOT_READY");
    signal.throwIfAborted();
    const start = parseUint256(request.startNonce, "startNonce");
    if (!Number.isSafeInteger(request.count) || request.count < 1 ||
      start + BigInt(request.count - 1) > MAX_UINT256) throw new Error("INVALID_CPU_POOL_RANGE");
    const abort = new AbortController();
    this.active = abort;
    const cancel = () => abort.abort();
    signal.addEventListener("abort", cancel, { once: true });
    let next = 0;
    let completed = 0;
    let candidate: BatchResult["candidate"] = null;
    const started = this.now();
    let lastReport = started;
    const report = () => {
      const now = this.now();
      if (now - lastReport >= 250) { lastReport = now; onProgress?.(completed); }
    };
    try {
      const settled = await Promise.allSettled(this.workers.map(async worker => {
        try {
          while (!abort.signal.aborted && next < request.count) {
            if (workDeadlineReached(request)) { cancel(); break; }
            const offset = next;
            const count = Math.min(CHUNK_SIZE, request.count - offset);
            next += count;
            let observed = 0;
            const observe = (value: number) => {
              if (!Number.isSafeInteger(value) || value < observed || value > count) throw new Error("INVALID_CPU_PROGRESS");
              completed += value - observed;
              observed = value;
              report();
            };
            const result = await worker.runBatch({ ...request, startNonce: (start + BigInt(offset)).toString(), count },
              abort.signal, observe);
            observe(result.completed);
            if (result.candidate && !candidate) {
              const nonce = parseUint256(result.candidate.nonce, "nonce");
              if (nonce < start + BigInt(offset) || nonce >= start + BigInt(offset + count)) {
                throw new Error("CPU_CANDIDATE_OUTSIDE_RANGE");
              }
              candidate = result.candidate;
              cancel();
            }
            if (result.cancelled && workDeadlineReached(request)) cancel();
            if (result.cancelled && !abort.signal.aborted) throw new Error("UNEXPECTED_CPU_CANCELLATION");
          }
        } catch (error) {
          // A sibling can find a proof while this worker is awaiting READY dispatch.
          if (abort.signal.aborted && error instanceof DOMException && error.name === "AbortError") return;
          cancel(); throw error;
        }
      }));
      // Final cumulative count also includes work done before cooperative cancellation.
      onProgress?.(completed);
      const failure = settled.find(result => result.status === "rejected");
      if (failure?.status === "rejected") throw failure.reason;
      return { completed, candidate, elapsedMs: Math.max(0, this.now() - started), cancelled: signal.aborted || workDeadlineReached(request) };
    } finally {
      signal.removeEventListener("abort", cancel);
      this.active = null;
    }
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.active?.abort();
    const workers = this.workers;
    this.workers = [];
    await Promise.allSettled(workers.map(worker => Promise.resolve().then(() => worker.dispose())));
  }
}
